/*
 * Copyright (c) 2023 Irian Software Development SRL. All Rights Reserved.
 * This software is the proprietary information of Irian Software Development SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { RoleRightDefinitionDto } from '../models/role-right-definition-dto.model';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { RoleWithRightsDto } from '../models/role-with-rights-dto.model';
import { RoleWithRightsVo } from '../models/role-with-rights-vo.model';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'auth-role-configuration-view',
  templateUrl: './role-configuration-view.component.html',
  styleUrls: ['./role-configuration-view.component.scss'],
})
export class RoleConfigurationViewComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() editedRole: RoleWithRightsDto | undefined = undefined;
  @Input() roleWithRightVOs: RoleWithRightsVo[] = [];
  @Input() assignedRightsMap: any = {};
  @Input() isPlatformAdmin = true;
  @Input() editMode = false;
  @Input() showCompanyTypeDropdownForPlatform = false;
  @Input() companyTypeOptions: SelectItem[] = [];
  @Input() companyTypeTranslationMap: { [s: string]: string } = {};

  @Output() submitRole: EventEmitter<RoleWithRightsDto> =
    new EventEmitter<RoleWithRightsDto>();
  @Output() delete: EventEmitter<string> = new EventEmitter<string>();
  @Output() edit: EventEmitter<string> = new EventEmitter<string>();
  @Output() duplicate: EventEmitter<string> = new EventEmitter<string>();

  rolesForm: FormGroup | undefined = undefined;
  rightFormArray: FormArray = new FormArray([]);
  rightNames: string[] = [];
  tableColumns: any[] = [];
  destroy: Subject<boolean> = new Subject<boolean>();
  roleRightDefinitions: RoleRightDefinitionDto[] = [];

  constructor(private readonly confirmationService: ConfirmationService) {}

  get companyTypeControl() {
    return this.rolesForm?.get('companyType') as FormControl;
  }

  ngOnInit() {
    this.rolesForm = new FormGroup<any>({
      uuid: new FormControl(null),
      name: new FormControl(null),
      description: new FormControl(null),
      platformRole: new FormControl(false),
      originalRoleDefinitionUuid: new FormControl(null),
      owner: new FormControl(null),
      validFrom: new FormControl(null),
      validTo: new FormControl(null),
      companyType: new FormControl(null),
    });
    this.rolesForm.addControl('roleRights', this.rightFormArray);
    this.listenToPlatformRoleChanges();
    this.listenToCompanyTypeValueChanges();
  }

  ngOnChanges() {
    console.log(this.editedRole);
    if (this.editedRole) {
      this.tableColumns = [
        'name',
        'description',
        'editedRole',
        ...this.roleWithRightVOs
      ];
      this.rightNames = this.editedRole.roleRights?.map(
        (roleRight: RoleRightDefinitionDto) => roleRight.rightName
      );
      this.setRights(this.editedRole.roleRights);
      this.rolesForm?.patchValue(this.editedRole);
      this.rolesForm?.updateValueAndValidity();
    }
  }

  setRights(rights: RoleRightDefinitionDto[]) {
    this.rightFormArray.clear();
    this.roleRightDefinitions = rights.sort(
      (roleRightDefinition1, roleRightDefinition2) =>
        roleRightDefinition1.rightName < roleRightDefinition2.rightName ? -1 : 1
    );
    for (const right of rights) {
      this.rightFormArray.push(
        new FormGroup({
          rightName: new FormControl(right.rightName),
          rightLabel: new FormControl(right.rightLabel),
          description: new FormControl(right.description),
          selected: new FormControl(right.selected),
          roleDefinitionUuid: new FormControl(right.roleDefinitionUuid),
        })
      );
    }
  }

  getPlatformRoleSwitchValue() {
    return this.rolesForm?.get('platformRole')?.value;
  }

  onSubmit() {
    this.rightFormArray.patchValue(this.roleRightDefinitions);
    this.rolesForm?.get('name')?.markAsDirty();
    this.companyTypeControl.markAsDirty();
    this.rolesForm?.updateValueAndValidity();
    if (this.rolesForm?.valid) {
      this.submitRole.emit(this.rolesForm.getRawValue());
      this.rolesForm.reset();
    }
  }

  onEdit(uuid: string) {
    this.edit.emit(uuid);
  }

  onDuplicate(uuid: string) {
    this.duplicate.emit(uuid);
  }

  onDelete(uuid: string) {
    this.confirmationService.confirm({
      accept: () => {
        this.delete.emit(uuid);
      },
    });
  }

  isRightReadonly(roleRightDefinition: RoleRightDefinitionDto) {
    return (
      this.companyTypeControl.value != null &&
      !roleRightDefinition.companyTypes.includes(this.companyTypeControl.value)
    );
  }

  private listenToPlatformRoleChanges() {
    this.rolesForm?.get('platformRole')?.valueChanges.pipe(takeUntil(this.destroy))
      .subscribe(() => {
        this.companyTypeControl.reset();
        this.updateCompanyTypeValidators();
      });
  }

  private listenToCompanyTypeValueChanges() {
    this.companyTypeControl.valueChanges
      .pipe(takeUntil(this.destroy))
      .subscribe(() => {
        const companyType = this.companyTypeControl.value;
        this.unselectRightsWithDifferentCompanyType(companyType);
      });
  }

  private unselectRightsWithDifferentCompanyType(companyType: string) {
    if (companyType == null) return;
    for (let i = 0; i < this.roleRightDefinitions.length; i++) {
      const right = this.roleRightDefinitions[i];
      if (!right.companyTypes.includes(companyType)) {
        right.selected = false;
      }
    }
  }

  private updateCompanyTypeValidators() {
    if (!this.showCompanyTypeDropdownForPlatform || this.editMode) {
      return;
    }

    if (this.getPlatformRoleSwitchValue()) {
      this.companyTypeControl.setValidators(Validators.required);
    } else {
      this.companyTypeControl.clearValidators();
    }
  }

  ngOnDestroy() {
    this.destroy.next(true);
  }
}
